import { useMemo, useState, useEffect, type ReactNode } from 'react';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Modal,
  Paper,
  Select,
  Skeleton,
  Stack,
  Tabs,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconCalendarDue,
  IconCreditCard,
  IconDownload,
  IconEye,
  IconFileDescription,
  IconInfoCircle,
  IconReceipt,
  IconShieldCheck,
  IconWallet,
} from '@tabler/icons-react';
import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';
import { SearchInput } from '@/components/SearchInput';
import { ClientPortalLayout } from './components/ClientPortalLayout';
import { BillingTabPanelLayout } from './components/BillingTabPanelLayout';
import { EditInsuranceModal } from './components/billing/EditInsuranceModal';
import { ClientPortalCardsSection } from './components/billing/ClientPortalCardsSection';
import { ClientPortalInsurancePlansSection } from './components/billing/ClientPortalInsurancePlansSection';
import { useClientPortalBillingSummary } from './hooks/useClientPortalBillingSummary';
import type {
  ClientPortalBillingDocument,
  ClientPortalBillingServiceLine,
  ClientPortalBillingSummary,
  ClientPortalCoverageSummary,
  ClientPortalInsurancePaymentSummary,
  ClientPortalInsurancePriority,
  ClientPortalInvoiceSummary,
  ClientPortalPaymentSummary,
} from './utils/clientPortalFhirTypes';
import {
  billingStatusColor,
  billingStatusLabel,
  documentKindLabel,
  formatBillingDate,
  formatMoney,
  formatServiceLineLabel,
  insurancePaymentStatusColor,
  insurancePaymentStatusLabel,
  paymentStatusColor,
  paymentStatusLabel,
  paymentChargeTypeLabel,
  resolveServiceLineAmountDue,
} from './utils/portalBillingModels';
import { clientFriendlyClaimStatus } from '@/lib/billing/billingTimelineFilter';
import type { BillingTimelineItem } from '@/lib/billing/billingTimelineTypes';
import { resolvePaymentServiceLineIds } from './utils/resolvePaymentServiceLineIds';
import { resolveBillingPageContent } from './utils/resolveBillingPageContent';
import {
  BillingActionNotImplementedError,
  createClientPortalPaymentIntent,
} from './api/clientPortalBillingActions';
import { FEATURE_VISIBILITY } from '@/config/featureVisibility';
import { LocalhostOnlyBadge } from '@/components/LocalhostOnlyBadge';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function handleBillingAction(action: () => Promise<unknown>) {
  try {
    await action();
  } catch (err) {
    showBillingNotice(err);
  }
}

function showBillingNotice(err: unknown) {
  const msg =
    err instanceof BillingActionNotImplementedError
      ? err.message
      : 'An error occurred. Please contact your care team for help with billing.';
  notifications.show({
    color: 'blue',
    title: 'Not yet available online',
    message: msg,
    autoClose: 6000,
  });
}

function PortalBillingEmptyState({
  icon,
  title,
  description,
}: {
  icon: ReactNode;
  title: string;
  description: string;
}): JSX.Element {
  return (
    <Stack align="center" gap="md" py="xl">
      <ThemeIcon size={72} radius="xl" color="gray" variant="light">
        {icon}
      </ThemeIcon>
      <Title order={3}>{title}</Title>
      <Text size="sm" c="gray.6" maw={340} ta="center">
        {description}
      </Text>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Table layout — filters above bordered card (caseload pattern)
// ---------------------------------------------------------------------------

interface BillingTableFilterConfig {
  placeholder: string;
  options: { value: string; label: string }[];
  columnKey: string;
  ariaLabel?: string;
  w?: number | string;
}

function getColumnDisplayValue<T>(col: UnifyTableColumn<T>, row: T): string {
  if (col.getValue) return col.getValue(row);
  const value = (row as Record<string, unknown>)[col.key];
  if (value == null) return '';
  return String(value);
}

function applyBillingTableFilters<T>(
  rows: T[],
  columns: UnifyTableColumn<T>[],
  search: string,
  filterConfigs: BillingTableFilterConfig[],
  filterValues: (string | null)[],
  includeRowIds?: string[],
  getRowKey?: (row: T) => string,
): T[] {
  let result = [...rows];

  if (includeRowIds?.length && getRowKey) {
    const allowed = new Set(includeRowIds);
    result = result.filter((row) => allowed.has(getRowKey(row)));
  }

  if (search.trim()) {
    const query = search.trim().toLowerCase();
    result = result.filter((row) =>
      columns.some((col) => getColumnDisplayValue(col, row).toLowerCase().includes(query)),
    );
  }

  filterConfigs.forEach((cfg, index) => {
    const value = filterValues[index];
    if (value == null || value === '') return;
    const col = columns.find((column) => column.key === cfg.columnKey);
    if (!col) return;
    result = result.filter((row) => getColumnDisplayValue(col, row) === value);
  });

  return result;
}

interface BillingTableFilterPreset {
  token: number;
  search?: string;
  columnFilters?: Record<string, string>;
  includeRowIds?: string[];
}

interface BillingTablePanelProps<T> {
  title: string;
  subtitle?: string;
  columns: UnifyTableColumn<T>[];
  rows: T[];
  getRowKey: (row: T) => string;
  searchPlaceholder: string;
  filterConfigs?: BillingTableFilterConfig[];
  sortable?: boolean;
  sortableColumnKeys?: string[];
  pagination?: boolean;
  defaultRowsPerPage?: number;
  alerts?: ReactNode;
  emptyState?: ReactNode;
  filterPreset?: BillingTableFilterPreset;
  /** Shown with a clear action when a cross-tab navigation filter is active. */
  navigationFilterMessage?: string;
  onClearNavigationFilter?: () => void;
}

function BillingTablePanel<T>({
  title,
  subtitle,
  columns,
  rows,
  getRowKey,
  searchPlaceholder,
  filterConfigs = [],
  sortable,
  sortableColumnKeys,
  pagination = true,
  defaultRowsPerPage = 10,
  alerts,
  emptyState,
  filterPreset,
  navigationFilterMessage,
  onClearNavigationFilter,
}: BillingTablePanelProps<T>): JSX.Element {
  const [search, setSearch] = useState('');
  const [filterValues, setFilterValues] = useState<(string | null)[]>(() =>
    filterConfigs.map(() => null),
  );

  useEffect(() => {
    if (!filterPreset) {
      setSearch('');
      setFilterValues(filterConfigs.map(() => null));
      return;
    }
    setSearch(filterPreset.search ?? '');
    if (filterPreset.columnFilters) {
      setFilterValues(
        filterConfigs.map((cfg) => filterPreset.columnFilters?.[cfg.columnKey] ?? null),
      );
    } else {
      setFilterValues(filterConfigs.map(() => null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when preset token changes or clears
  }, [filterPreset?.token]);

  const activeSearch = filterPreset?.search ?? search;
  const activeIncludeRowIds = filterPreset?.includeRowIds;
  const activeFilterValues = useMemo(
    () => (
      filterPreset?.columnFilters
        ? filterConfigs.map((cfg) => filterPreset.columnFilters?.[cfg.columnKey] ?? null)
        : filterValues
    ),
    [filterPreset?.columnFilters, filterConfigs, filterValues],
  );

  const filteredRows = useMemo(
    () => applyBillingTableFilters(
      rows,
      columns,
      activeSearch,
      filterConfigs,
      activeFilterValues,
      activeIncludeRowIds,
      getRowKey,
    ),
    [rows, columns, activeSearch, filterConfigs, activeFilterValues, activeIncludeRowIds, getRowKey],
  );

  const navigationFilterAlert =
    navigationFilterMessage && onClearNavigationFilter ? (
      <Alert variant="light" color="blue" icon={<IconInfoCircle size={16} />}>
        <Group justify="space-between" align="center" wrap="wrap" gap="sm">
          <Text size="sm">{navigationFilterMessage}</Text>
          <Button variant="subtle" size="compact-sm" onClick={onClearNavigationFilter}>
            Show all
          </Button>
        </Group>
      </Alert>
    ) : null;

  const combinedAlerts =
    navigationFilterAlert || alerts ? (
      <Stack gap="sm">
        {navigationFilterAlert}
        {alerts}
      </Stack>
    ) : undefined;

  if (rows.length === 0 && emptyState) {
    return (
      <BillingTabPanelLayout alerts={combinedAlerts}>
        {emptyState}
      </BillingTabPanelLayout>
    );
  }

  return (
    <BillingTabPanelLayout
      filters={
        <>
          <SearchInput
            placeholder={searchPlaceholder}
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            w={280}
          />
          {filterConfigs.map((cfg, index) => (
            <Select
              key={cfg.columnKey}
              placeholder={cfg.placeholder}
              data={cfg.options}
              value={filterValues[index] ?? null}
              onChange={(value) => {
                setFilterValues((prev) => {
                  const next = [...prev];
                  next[index] = value;
                  return next;
                });
              }}
              clearable
              w={cfg.w ?? 200}
              aria-label={cfg.ariaLabel ?? cfg.placeholder}
            />
          ))}
        </>
      }
      alerts={combinedAlerts}
    >
      <UnifyTable
        withPaper={false}
        title={title}
        subtitle={subtitle}
        columns={columns}
        data={filteredRows}
        getRowKey={getRowKey}
        sortable={sortable}
        sortableColumnKeys={sortableColumnKeys}
        pagination={pagination}
        defaultRowsPerPage={defaultRowsPerPage}
      />
    </BillingTabPanelLayout>
  );
}

// ---------------------------------------------------------------------------
// Balance summary
// ---------------------------------------------------------------------------

function BillingBalanceSummaryCard({ summary }: { summary: ClientPortalBillingSummary }) {
  const hasDue = summary.balanceDueCents > 0;

  return (
    <Paper withBorder p="lg" radius="md" bg="gold.0">
      <Stack gap="md">
        <Group justify="space-between" wrap="wrap" gap="md">
          <Stack gap="xs">
            <Text size="xs" tt="uppercase" fw={700} c="dimmed">
              Current balance
            </Text>
            <Title order={2} c={hasDue ? 'gold.8' : 'gray.9'}>
              {formatMoney(summary.balanceDueCents)}
            </Title>
            {summary.nextDueDate && hasDue && (
              <Group gap={6}>
                <IconCalendarDue size={14} color="var(--mantine-color-gray-6)" />
                <Text size="sm" c="gray.6">
                  Due {formatBillingDate(summary.nextDueDate)}
                </Text>
              </Group>
            )}
            {!hasDue && (
              <Group gap={6}>
                <IconShieldCheck size={14} color="var(--mantine-color-gray-7)" />
                <Text size="sm" c="gray.7" fw={500}>
                  Paid in full
                </Text>
              </Group>
            )}
          </Stack>

          <Stack gap="xs" align="stretch" w={{ base: '100%', sm: 'auto' }}>
            <Button
              color="gold"
              leftSection={<IconCreditCard size={18} />}
              onClick={() => void handleBillingAction(() => createClientPortalPaymentIntent())}
              disabled={!hasDue}
            >
              Pay now
            </Button>
          </Stack>
        </Group>

        {(summary.insurancePendingCents ?? 0) > 0 && (
          <Alert variant="light" color="violet" icon={<IconShieldCheck size={16} />}>
            <Text size="sm">
              {formatMoney(summary.insurancePendingCents)} pending from insurance
            </Text>
          </Alert>
        )}
      </Stack>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Payment methods tab
// ---------------------------------------------------------------------------

function PaymentMethodsPanel({
  summary,
  onRefresh,
}: {
  summary: ClientPortalBillingSummary;
  onRefresh: () => void;
}) {
  const [editingInsurance, setEditingInsurance] = useState<ClientPortalCoverageSummary | null>(null);

  const openInsuranceEditor = (coverage: ClientPortalCoverageSummary) => {
    setEditingInsurance(coverage);
  };

  const openAddInsurance = (priority: ClientPortalInsurancePriority) => {
    setEditingInsurance({
      id: `new-${priority}`,
      priority,
      payerName: '',
      status: 'unknown',
    });
  };

  return (
    <>
      <Stack gap="xl">
        {FEATURE_VISIBILITY.clientPortalPaymentMethodsInsurance ? (
          <ClientPortalInsurancePlansSection
            coverageSummaries={summary.coverageSummaries}
            onEditCoverage={openInsuranceEditor}
            onAddCoverage={openAddInsurance}
            localhostOnly
          />
        ) : null}

        <ClientPortalCardsSection
          paymentMethods={summary.paymentMethods}
          onError={showBillingNotice}
        />
      </Stack>

      {FEATURE_VISIBILITY.clientPortalPaymentMethodsInsurance ? (
        <EditInsuranceModal
          opened={editingInsurance != null}
          coverage={editingInsurance}
          onClose={() => setEditingInsurance(null)}
          onError={showBillingNotice}
          onSaved={onRefresh}
        />
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Invoices tab
// ---------------------------------------------------------------------------

type InvoiceTableRow = ClientPortalInvoiceSummary & { statusLabel: string };

function InvoicesPanel({ summary }: { summary: ClientPortalBillingSummary }) {
  const invoiceRows = useMemo(
    (): InvoiceTableRow[] =>
      [...summary.openInvoices, ...summary.invoiceHistory].map((inv) => ({
        ...inv,
        statusLabel: billingStatusLabel(inv.status),
      })),
    [summary],
  );

  const columns = useMemo(
    (): UnifyTableColumn<InvoiceTableRow>[] => [
      {
        key: 'description',
        label: 'Description',
        render: (inv) => (
          <Stack gap={2}>
            <Text size="sm" fw={600} lineClamp={1}>
              {inv.description}
            </Text>
            {inv.invoiceNumber && (
              <Text size="xs" c="gray.6">
                {inv.invoiceNumber}
              </Text>
            )}
          </Stack>
        ),
        getValue: (inv) => `${inv.description} ${inv.invoiceNumber ?? ''}`,
      },
      {
        key: 'date',
        label: 'Date',
        render: (inv) => <Text size="sm" c="gray.6">{formatBillingDate(inv.date)}</Text>,
        getValue: (inv) => inv.date ?? '',
      },
      {
        key: 'dueDate',
        label: 'Due',
        render: (inv) => <Text size="sm" c="gray.6">{formatBillingDate(inv.dueDate)}</Text>,
        getValue: (inv) => inv.dueDate ?? '',
      },
      {
        key: 'amountDueCents',
        label: 'Balance due',
        render: (inv) => (
          <Text size="sm" fw={600} c={inv.amountDueCents > 0 ? 'gold.8' : 'green.8'}>
            {formatMoney(inv.amountDueCents)}
          </Text>
        ),
        getValue: (inv) => formatMoney(inv.amountDueCents),
        align: 'right',
      },
      {
        key: 'statusLabel',
        label: 'Status',
        render: (inv) => (
          <Badge variant="light" size="sm" color={billingStatusColor(inv.status)}>
            {inv.statusLabel}
          </Badge>
        ),
        getValue: (inv) => inv.statusLabel,
      },
      {
        key: 'actions',
        label: '',
        sortable: false,
        pinRight: true,
        render: (inv) =>
          inv.canPay ? (
            <Group justify="flex-end">
              <Button
                size="xs"
                variant="light"
                color="gold"
                leftSection={<IconCreditCard size={14} />}
                onClick={() => void handleBillingAction(() => createClientPortalPaymentIntent(inv.id))}
              >
                Pay {formatMoney(inv.amountDueCents)}
              </Button>
            </Group>
          ) : null,
        getValue: () => '',
      },
    ],
    [],
  );

  return (
    <BillingTablePanel
      title="Invoices"
      subtitle="Open and past invoices for your account"
      columns={columns}
      rows={invoiceRows}
      getRowKey={(inv) => inv.id}
      searchPlaceholder="Search by description or invoice #…"
      sortable
      sortableColumnKeys={['date', 'dueDate', 'amountDueCents', 'statusLabel']}
      filterConfigs={[
        {
          placeholder: 'All statuses',
          columnKey: 'statusLabel',
          options: [
            { value: 'Balance due', label: 'Balance due' },
            { value: 'Overdue', label: 'Overdue' },
            { value: 'Partially paid', label: 'Partially paid' },
            { value: 'Processing', label: 'Processing' },
            { value: 'Insurance pending', label: 'Insurance pending' },
            { value: 'Paid', label: 'Paid' },
            { value: 'Refunded', label: 'Refunded' },
          ],
          ariaLabel: 'Filter by status',
          w: 180,
        },
      ]}
      emptyState={
        <PortalBillingEmptyState
          icon={<IconReceipt size={36} />}
          title="No invoices yet"
          description="Invoices for your care will appear here once billing is available."
        />
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Services tab
// ---------------------------------------------------------------------------

type ServiceTableRow = ClientPortalBillingServiceLine & {
  serviceLabel: string;
  statusLabel: string;
  encounterKindLabel: string;
  balanceDueLabel: string;
  copayLabel?: string;
  claimLabel?: string;
  claimNumber?: string;
  cardMeta?: string;
};

function formatCardMeta(brand?: string, last4?: string): string | undefined {
  if (!brand && !last4) return undefined;
  if (brand && last4) return `${brand} •••• ${last4}`;
  if (brand) return brand;
  return `•••• ${last4}`;
}

function resolveCopayLabel(
  line: ClientPortalBillingServiceLine,
  timelineItem?: BillingTimelineItem,
): string {
  const paidCents = line.clientPaidCents ?? timelineItem?.clientPaid;
  if (paidCents != null && paidCents > 0) return formatMoney(paidCents);
  const owedCents = line.clientResponsibilityCents ?? timelineItem?.clientResponsibility;
  if (owedCents != null && owedCents > 0) return formatMoney(owedCents);
  return '—';
}

function ServicesPanel({
  lines,
  timeline,
  onClaimNavigate,
  filterPreset,
  onClearNavigationFilter,
}: {
  lines: ClientPortalBillingServiceLine[];
  timeline: BillingTimelineItem[];
  onClaimNavigate?: (claimNumber: string) => void;
  filterPreset?: BillingTableFilterPreset;
  onClearNavigationFilter?: () => void;
}) {
  const timelineByCharge = useMemo(() => {
    const map = new Map<string, BillingTimelineItem>();
    for (const item of timeline) {
      if (item.id.startsWith('charge-')) {
        map.set(item.id.replace('charge-', ''), item);
      }
    }
    return map;
  }, [timeline]);

  const serviceRows = useMemo(
    (): ServiceTableRow[] =>
      lines.map((line) => {
        const balanceDueCents = resolveServiceLineAmountDue(line);
        const timelineItem =
          (line.chargeItemId ? timelineByCharge.get(line.chargeItemId) : undefined) ??
          timeline.find((t) => t.sourceReference?.reference === `Encounter/${line.encounterId}`);
        const copayLabel = resolveCopayLabel(line, timelineItem);
        const cardMeta = formatCardMeta(timelineItem?.cardBrand, timelineItem?.cardLast4);
        const claimNumber = timelineItem?.clientClaimNumber;
        return {
          ...line,
          serviceLabel: formatServiceLineLabel(line),
          statusLabel: billingStatusLabel(line.status),
          encounterKindLabel: line.encounterKind === 'assessment' ? 'Assessment' : 'Appointment',
          balanceDueLabel: formatMoney(balanceDueCents),
          copayLabel,
          claimLabel: clientFriendlyClaimStatus(timelineItem?.clientVisibleStatus ?? line.status),
          claimNumber,
          cardMeta,
        };
      }),
    [lines, timeline, timelineByCharge],
  );

  const columns = useMemo(
    (): UnifyTableColumn<ServiceTableRow>[] => [
      {
        key: 'dateOfService',
        label: 'Date of service',
        render: (line) => <Text size="sm" c="gray.6">{formatBillingDate(line.dateOfService)}</Text>,
        getValue: (line) => line.dateOfService,
      },
      {
        key: 'serviceLabel',
        label: 'Service',
        render: (line) => (
          <Text size="sm" fw={600} lineClamp={2}>
            {line.serviceLabel}
          </Text>
        ),
        getValue: (line) => line.serviceLabel,
      },
      {
        key: 'provider',
        label: 'Provider',
        render: (line) => <Text size="sm" c="gray.6">{line.provider ?? '—'}</Text>,
        getValue: (line) => line.provider ?? '',
      },
      {
        key: 'copayLabel',
        label: 'Copay',
        render: (line) => <Text size="sm">{line.copayLabel ?? '—'}</Text>,
        getValue: (line) => line.copayLabel ?? '',
      },
      {
        key: 'cardMeta',
        label: 'Card',
        render: (line) => <Text size="sm" c="gray.6">{line.cardMeta ?? '—'}</Text>,
        getValue: (line) => line.cardMeta ?? '',
      },
      {
        key: 'claimLabel',
        label: 'Insurance',
        render: (line) => <Text size="sm">{line.claimLabel ?? '—'}</Text>,
        getValue: (line) => line.claimLabel ?? '',
      },
      {
        key: 'claimNumber',
        label: 'Claim',
        width: 132,
        render: (line) => (
          line.claimNumber && onClaimNavigate ? (
            <Anchor
              component="button"
              type="button"
              size="sm"
              style={{ whiteSpace: 'nowrap' }}
              onClick={() => onClaimNavigate(line.claimNumber!)}
            >
              {line.claimNumber}
            </Anchor>
          ) : (
            <Text size="sm" c="gray.6">—</Text>
          )
        ),
        getValue: (line) => line.claimNumber ?? '',
      },
      {
        key: 'balanceDueLabel',
        label: 'Balance due',
        render: (line) => {
          const balanceDueCents = resolveServiceLineAmountDue(line);
          return (
            <Text size="sm" fw={600} c={balanceDueCents > 0 ? 'gold.8' : 'green.8'}>
              {line.balanceDueLabel}
            </Text>
          );
        },
        getValue: (line) => line.balanceDueLabel,
        align: 'right',
      },
      {
        key: 'statusLabel',
        label: 'Status',
        render: (line) => (
          <Badge variant="light" size="sm" color={billingStatusColor(line.status)}>
            {line.statusLabel}
          </Badge>
        ),
        getValue: (line) => line.statusLabel,
      },
    ],
    [onClaimNavigate],
  );

  return (
    <BillingTablePanel
      title="Services"
      subtitle="Appointments and assessments by episode of care"
      columns={columns}
      rows={serviceRows}
      getRowKey={(line) => line.id}
      searchPlaceholder="Search by service, episode, or provider…"
      sortable
      sortableColumnKeys={['dateOfService', 'serviceLabel', 'balanceDueLabel', 'statusLabel']}
      filterPreset={filterPreset}
      navigationFilterMessage={
        (filterPreset?.includeRowIds?.length ?? 0) > 0
          ? `Showing ${filterPreset!.includeRowIds!.length} service${
            filterPreset!.includeRowIds!.length === 1 ? '' : 's'
          } linked to this payment.`
          : undefined
      }
      onClearNavigationFilter={onClearNavigationFilter}
      filterConfigs={[
        {
          placeholder: 'All types',
          columnKey: 'encounterKindLabel',
          options: [
            { value: 'Appointment', label: 'Appointments' },
            { value: 'Assessment', label: 'Assessments' },
          ],
          ariaLabel: 'Filter by type',
          w: 160,
        },
        {
          placeholder: 'All statuses',
          columnKey: 'statusLabel',
          options: [
            { value: 'Balance due', label: 'Balance due' },
            { value: 'Paid', label: 'Paid' },
            { value: 'Insurance pending', label: 'Insurance pending' },
            { value: 'Unknown', label: 'No charge' },
          ],
          ariaLabel: 'Filter by status',
          w: 180,
        },
      ]}
      emptyState={
        <PortalBillingEmptyState
          icon={<IconCalendarDue size={36} />}
          title="No services yet"
          description="Your session and assessment history will appear here once available."
        />
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Statements tab
// ---------------------------------------------------------------------------

type StatementTableRow = ClientPortalBillingDocument & { kindLabel: string };

function StatementsPanel({ summary }: { summary: ClientPortalBillingSummary }) {
  const statementRows = useMemo(
    (): StatementTableRow[] =>
      summary.documents
        .filter((doc) => doc.kind !== 'eob' && doc.kind !== 'gfe')
        .map((doc) => ({
          ...doc,
          kindLabel: documentKindLabel(doc.kind),
        })),
    [summary.documents],
  );

  const columns = useMemo(
    (): UnifyTableColumn<StatementTableRow>[] => [
      {
        key: 'title',
        label: 'Document',
        render: (doc) => (
          <Stack gap={2}>
            <Text size="sm" fw={600} lineClamp={1}>
              {doc.title}
            </Text>
            {doc.periodLabel && (
              <Text size="xs" c="gray.6">
                {doc.periodLabel}
              </Text>
            )}
          </Stack>
        ),
        getValue: (doc) => `${doc.title} ${doc.periodLabel ?? ''}`,
      },
      {
        key: 'kindLabel',
        label: 'Type',
        render: (doc) => (
          <Badge variant="light" size="sm" color="gray">
            {doc.kindLabel}
          </Badge>
        ),
        getValue: (doc) => doc.kindLabel,
      },
      {
        key: 'date',
        label: 'Date',
        render: (doc) => <Text size="sm" c="gray.6">{formatBillingDate(doc.date)}</Text>,
        getValue: (doc) => doc.date ?? '',
      },
      {
        key: 'download',
        label: '',
        sortable: false,
        pinRight: true,
        render: (doc) => (
          <Group justify="flex-end">
            <Button
              size="xs"
              variant="light"
              color={doc.url ? 'gold' : 'gray'}
              leftSection={<IconDownload size={14} />}
              disabled={!doc.url}
              onClick={() => {
                if (doc.url) window.open(doc.url, '_blank', 'noopener,noreferrer');
              }}
            >
              {doc.url ? 'Download' : 'Not yet available'}
            </Button>
          </Group>
        ),
        getValue: () => '',
      },
    ],
    [],
  );

  return (
    <BillingTablePanel
      title="Statements"
      subtitle="Statements, superbills, and receipts"
      columns={columns}
      rows={statementRows}
      getRowKey={(doc) => doc.id}
      searchPlaceholder="Search statements…"
      sortable
      sortableColumnKeys={['title', 'kindLabel', 'date']}
      filterConfigs={[
        {
          placeholder: 'All types',
          columnKey: 'kindLabel',
          options: [
            { value: 'Statement', label: 'Statements' },
            { value: 'Superbill', label: 'Superbills' },
            { value: 'Receipt', label: 'Receipts' },
          ],
          ariaLabel: 'Filter by document type',
          w: 220,
        },
      ]}
      emptyState={
        <PortalBillingEmptyState
          icon={<IconFileDescription size={36} />}
          title="No statements yet"
          description="Patient statements, superbills, and receipts will appear here once available."
        />
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Client payments tab
// ---------------------------------------------------------------------------

type ClientPaymentTableRow = ClientPortalPaymentSummary & {
  statusLabel: string;
  chargeTypeLabel: string;
};

function ClientPaymentsPanel({
  summary,
  onServiceNavigate,
  filterPreset,
}: {
  summary: ClientPortalBillingSummary;
  onServiceNavigate?: (payment: ClientPortalPaymentSummary) => void;
  filterPreset?: BillingTableFilterPreset;
}) {
  const paymentRows = useMemo(
    (): ClientPaymentTableRow[] =>
      summary.recentPayments.map((payment) => ({
        ...payment,
        statusLabel: paymentStatusLabel(payment.status),
        chargeTypeLabel: paymentChargeTypeLabel(payment.chargeType),
      })),
    [summary.recentPayments],
  );

  const columns = useMemo(
    (): UnifyTableColumn<ClientPaymentTableRow>[] => [
      {
        key: 'date',
        label: 'Date',
        render: (payment) => <Text size="sm" c="gray.6">{formatBillingDate(payment.date)}</Text>,
        getValue: (payment) => payment.date,
      },
      {
        key: 'serviceLabel',
        label: 'Service(s)',
        render: (payment) => (
          onServiceNavigate && payment.serviceLineIds?.length ? (
            <Anchor
              component="button"
              type="button"
              size="sm"
              fw={600}
              lineClamp={2}
              style={{ textAlign: 'left' }}
              onClick={() => onServiceNavigate(payment)}
            >
              {payment.serviceLabel}
            </Anchor>
          ) : (
            <Text size="sm" fw={600} lineClamp={2}>
              {payment.serviceLabel}
            </Text>
          )
        ),
        getValue: (payment) => payment.serviceLabel,
      },
      {
        key: 'chargeTypeLabel',
        label: 'Payment type',
        render: (payment) => (
          <Text size="sm">
            {payment.chargeTypeLabel}
          </Text>
        ),
        getValue: (payment) => payment.chargeTypeLabel,
      },
      {
        key: 'methodLabel',
        label: 'Payment method',
        render: (payment) => (
          <Text size="sm" fw={600}>
            {payment.methodLabel ?? '—'}
          </Text>
        ),
        getValue: (payment) => payment.methodLabel ?? '',
      },
      {
        key: 'amountCents',
        label: 'Amount',
        render: (payment) => (
          <Text size="sm" fw={600}>
            {formatMoney(payment.amountCents)}
          </Text>
        ),
        getValue: (payment) => formatMoney(payment.amountCents),
        align: 'right',
      },
      {
        key: 'statusLabel',
        label: 'Status',
        render: (payment) => (
          <Badge variant="light" size="sm" color={paymentStatusColor(payment.status)}>
            {payment.statusLabel}
          </Badge>
        ),
        getValue: (payment) => payment.statusLabel,
      },
    ],
    [onServiceNavigate],
  );

  return (
    <BillingTablePanel
      title="Client payment history"
      subtitle="Recent payments made by card or bank account"
      columns={columns}
      rows={paymentRows}
      getRowKey={(payment) => payment.id}
      searchPlaceholder="Search by service or payment method…"
      sortable
      sortableColumnKeys={['date', 'serviceLabel', 'chargeTypeLabel', 'amountCents', 'statusLabel']}
      filterPreset={filterPreset}
      filterConfigs={[
        {
          placeholder: 'All payment types',
          columnKey: 'chargeTypeLabel',
          options: [
            { value: 'Auto-charge', label: 'Auto-charge' },
            { value: 'Manual payment', label: 'Manual payment' },
          ],
          ariaLabel: 'Filter by payment type',
          w: 180,
        },
        {
          placeholder: 'All statuses',
          columnKey: 'statusLabel',
          options: [
            { value: 'Paid', label: 'Paid' },
            { value: 'Processing', label: 'Processing' },
            { value: 'Scheduled', label: 'Scheduled' },
            { value: 'Failed', label: 'Failed' },
            { value: 'Refunded', label: 'Refunded' },
          ],
          ariaLabel: 'Filter by status',
          w: 180,
        },
      ]}
      emptyState={
        <PortalBillingEmptyState
          icon={<IconReceipt size={36} />}
          title="No client payments yet"
          description="Payments you make through the portal will appear here."
        />
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Insurance payments tab
// ---------------------------------------------------------------------------

type InsurancePaymentTableRow = ClientPortalInsurancePaymentSummary & {
  statusLabel: string;
  claimNumberLabel: string;
};

function InsurancePaymentsPanel({
  summary,
  filterPreset,
  onClearNavigationFilter,
}: {
  summary: ClientPortalBillingSummary;
  filterPreset?: BillingTableFilterPreset;
  onClearNavigationFilter?: () => void;
}) {
  const [eobModalDoc, setEobModalDoc] = useState<ClientPortalBillingDocument | null>(null);

  const eobById = useMemo(() => {
    const map = new Map<string, ClientPortalBillingDocument>();
    for (const doc of summary.eobs) {
      map.set(doc.id, doc);
    }
    return map;
  }, [summary.eobs]);

  const paymentRows = useMemo(
    (): InsurancePaymentTableRow[] =>
      summary.insurancePayments.map((payment) => ({
        ...payment,
        statusLabel: insurancePaymentStatusLabel(payment.status),
        claimNumberLabel: payment.claimNumber ?? '',
      })),
    [summary.insurancePayments],
  );

  const columns = useMemo(
    (): UnifyTableColumn<InsurancePaymentTableRow>[] => [
      {
        key: 'date',
        label: 'Payment date',
        render: (payment) => <Text size="sm" c="gray.6">{formatBillingDate(payment.date)}</Text>,
        getValue: (payment) => payment.date,
      },
      {
        key: 'serviceLabel',
        label: 'Service',
        render: (payment) => (
          <Stack gap={2}>
            <Text size="sm" fw={600}>
              {payment.serviceLabel}
            </Text>
            {payment.dateOfService && (
              <Text size="xs" c="gray.6">
                Date of service {formatBillingDate(payment.dateOfService)}
              </Text>
            )}
          </Stack>
        ),
        getValue: (payment) => payment.serviceLabel,
      },
      {
        key: 'claimNumberLabel',
        label: 'Claim',
        width: 132,
        render: (payment) => (
          <Text size="sm" fw={500} style={{ whiteSpace: 'nowrap' }}>
            {payment.claimNumber ?? '—'}
          </Text>
        ),
        getValue: (payment) => payment.claimNumberLabel,
      },
      {
        key: 'amountCents',
        label: 'Amount',
        render: (payment) => (
          <Text size="sm" fw={600}>
            {formatMoney(payment.amountCents)}
          </Text>
        ),
        getValue: (payment) => formatMoney(payment.amountCents),
        align: 'right',
      },
      {
        key: 'statusLabel',
        label: 'Status',
        render: (payment) => (
          <Badge variant="light" size="sm" color={insurancePaymentStatusColor(payment.status)}>
            {payment.statusLabel}
          </Badge>
        ),
        getValue: (payment) => payment.statusLabel,
      },
      {
        key: 'eob',
        label: '',
        sortable: false,
        pinRight: true,
        render: (payment) => (
          <Group justify="flex-end">
            <Button
              size="xs"
              variant="light"
              color={payment.eobDocumentId ? 'gold' : 'gray'}
              leftSection={<IconEye size={14} />}
              disabled={!payment.eobDocumentId || !eobById.has(payment.eobDocumentId)}
              onClick={() => {
                if (!payment.eobDocumentId) return;
                const doc = eobById.get(payment.eobDocumentId);
                if (doc) setEobModalDoc(doc);
              }}
            >
              View EOB
            </Button>
          </Group>
        ),
        getValue: () => '',
      },
    ],
    [eobById],
  );

  if (paymentRows.length === 0) {
    return (
      <BillingTablePanel
        title="Insurance payments"
        subtitle="Payments from your insurer, linked to services and EOBs"
        columns={columns}
        rows={[]}
        getRowKey={(payment) => payment.id}
        searchPlaceholder="Search by service or status…"
        emptyState={
          <PortalBillingEmptyState
            icon={<IconShieldCheck size={36} />}
            title="No insurance payments yet"
            description="Insurance payments and linked EOBs will appear here once claims are processed."
          />
        }
      />
    );
  }

  return (
    <>
      <BillingTablePanel
        title="Insurance payments"
        subtitle="Payments from your insurer, linked to services and EOBs"
        columns={columns}
        rows={paymentRows}
        getRowKey={(payment) => payment.id}
        searchPlaceholder="Search by service, claim, or status…"
        sortable
        sortableColumnKeys={['date', 'serviceLabel', 'claimNumberLabel', 'amountCents', 'statusLabel']}
        filterPreset={filterPreset}
        navigationFilterMessage={
          filterPreset?.columnFilters?.claimNumberLabel
            ? `Showing insurance payments for claim ${filterPreset.columnFilters.claimNumberLabel}.`
            : undefined
        }
        onClearNavigationFilter={onClearNavigationFilter}
        filterConfigs={[
          {
            placeholder: 'All claims',
            columnKey: 'claimNumberLabel',
            options: [...new Set(paymentRows.map((p) => p.claimNumberLabel).filter(Boolean))].map((value) => ({
              value,
              label: value,
            })),
            ariaLabel: 'Filter by claim',
            w: 180,
          },
          {
            placeholder: 'All statuses',
            columnKey: 'statusLabel',
            options: [
              { value: 'Paid', label: 'Paid' },
              { value: 'Pending', label: 'Pending' },
              { value: 'Processing', label: 'Processing' },
              { value: 'Denied', label: 'Denied' },
              { value: 'Partially paid', label: 'Partially paid' },
            ],
            ariaLabel: 'Filter by status',
            w: 180,
          },
        ]}
        alerts={
          (summary.insurancePendingCents ?? 0) > 0 ? (
            <Alert variant="light" color="violet" icon={<IconShieldCheck size={16} />}>
              <Text size="sm">
                {formatMoney(summary.insurancePendingCents)} is pending from your insurance.
              </Text>
            </Alert>
          ) : undefined
        }
      />

      <Modal
        opened={eobModalDoc != null}
        onClose={() => setEobModalDoc(null)}
        title="Explanation of Benefits"
        centered
      >
        {eobModalDoc && (
          <Stack gap="md">
            <Text fw={600}>{eobModalDoc.title}</Text>
            {eobModalDoc.date && (
              <Text size="sm" c="gray.6">
                {formatBillingDate(eobModalDoc.date)}
              </Text>
            )}
            <Group justify="flex-end" gap="sm">
              <Button variant="default" onClick={() => setEobModalDoc(null)}>
                Close
              </Button>
              <Button
                variant="light"
                color="gold"
                leftSection={<IconDownload size={14} />}
                disabled={!eobModalDoc.url}
                onClick={() => {
                  if (eobModalDoc.url) {
                    window.open(eobModalDoc.url, '_blank', 'noopener,noreferrer');
                  }
                }}
              >
                {eobModalDoc.url ? 'Download' : 'Download not yet available'}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
}

// ---------------------------------------------------------------------------
// Loading & empty states
// ---------------------------------------------------------------------------

function BillingLoadingSkeleton() {
  return (
    <Stack gap="lg">
      <Skeleton height={140} radius="md" />
      <Skeleton height={48} radius="md" />
      <Skeleton height={280} radius="md" />
    </Stack>
  );
}

function BillingEmptyState() {
  return (
    <Paper withBorder p="xl" radius="md">
      <PortalBillingEmptyState
        icon={<IconReceipt size={36} />}
        title="No billing activity yet"
        description="Future invoices, statements, superbills, and payment history will appear here once available."
      />
    </Paper>
  );
}

const BILLING_LEDGER_TABS = new Set([
  'invoices',
  'services',
  'statements',
  'client-payments',
  'insurance-payments',
]);

function localhostBillingTabLabel(label: string): ReactNode {
  return (
    <Group gap={6} wrap="nowrap">
      <span>{label}</span>
      <LocalhostOnlyBadge />
    </Group>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function ClientPortalBillingPage(): JSX.Element {
  const { summary, loading, error, refresh } = useClientPortalBillingSummary();
  const pageContent = resolveBillingPageContent({ loading, error, summary });
  const showBillingLedger = FEATURE_VISIBILITY.clientPortalBillingLedger;
  const [activeTab, setActiveTab] = useState<string | null>(
    showBillingLedger ? 'invoices' : 'payment-methods',
  );
  const [servicesFilterPreset, setServicesFilterPreset] = useState<BillingTableFilterPreset | undefined>();
  const [insuranceFilterPreset, setInsuranceFilterPreset] = useState<BillingTableFilterPreset | undefined>();

  useEffect(() => {
    if (!showBillingLedger && activeTab && BILLING_LEDGER_TABS.has(activeTab)) {
      setActiveTab('payment-methods');
    }
  }, [activeTab, showBillingLedger]);

  const navigateToInsuranceClaim = (claimNumber: string) => {
    setInsuranceFilterPreset({ token: Date.now(), columnFilters: { claimNumberLabel: claimNumber } });
    setActiveTab('insurance-payments');
  };

  const navigateToServices = (payment: ClientPortalPaymentSummary) => {
    if (!summary) return;
    const resolvedIds = resolvePaymentServiceLineIds(summary, payment);
    setServicesFilterPreset({
      token: Date.now(),
      includeRowIds: resolvedIds.length > 0 ? resolvedIds : undefined,
    });
    setActiveTab('services');
  };

  return (
    <ClientPortalLayout
      title="Billing"
      description="View balances, invoices, services, statements, and payment history."
    >
      <Stack gap="lg">
        {pageContent === 'loading' ? (
          <BillingLoadingSkeleton />
        ) : pageContent === 'error' ? (
          <Alert
            variant="light"
            color="red"
            icon={<IconAlertCircle size={16} />}
            title="Could not load billing"
          >
            <Text size="sm">{error}</Text>
          </Alert>
        ) : pageContent === 'unavailable' || !summary ? (
          <BillingEmptyState />
        ) : (
          <>
            <BillingBalanceSummaryCard summary={summary} />

            <Tabs value={activeTab} onChange={setActiveTab} keepMounted={false}>
              <Tabs.List>
                {showBillingLedger ? (
                  <Tabs.Tab value="invoices" leftSection={<IconReceipt size={14} />}>
                    {localhostBillingTabLabel('Invoices')}
                    {summary.openInvoices.length > 0 && (
                      <Badge size="xs" color="gold" variant="filled" ml={6}>
                        {summary.openInvoices.length}
                      </Badge>
                    )}
                  </Tabs.Tab>
                ) : null}
                {showBillingLedger ? (
                  <Tabs.Tab value="services" leftSection={<IconCalendarDue size={14} />}>
                    {localhostBillingTabLabel('Services')}
                  </Tabs.Tab>
                ) : null}
                {showBillingLedger ? (
                  <Tabs.Tab value="statements" leftSection={<IconFileDescription size={14} />}>
                    {localhostBillingTabLabel('Statements')}
                  </Tabs.Tab>
                ) : null}
                <Tabs.Tab value="payment-methods" leftSection={<IconWallet size={14} />}>
                  Payment Methods
                </Tabs.Tab>
                {showBillingLedger ? (
                  <Tabs.Tab value="client-payments" leftSection={<IconCreditCard size={14} />}>
                    {localhostBillingTabLabel('Client Payments')}
                  </Tabs.Tab>
                ) : null}
                {showBillingLedger ? (
                  <Tabs.Tab value="insurance-payments" leftSection={<IconShieldCheck size={14} />}>
                    {localhostBillingTabLabel('Insurance Payments')}
                  </Tabs.Tab>
                ) : null}
              </Tabs.List>

              {showBillingLedger ? (
                <Tabs.Panel value="invoices" pt="md">
                  <InvoicesPanel summary={summary} />
                </Tabs.Panel>
              ) : null}

              {showBillingLedger ? (
                <Tabs.Panel value="services" pt="md">
                  <ServicesPanel
                    key={servicesFilterPreset?.token ?? 'services-default'}
                    lines={summary.serviceLines}
                    timeline={summary.billingTimeline}
                    onClaimNavigate={navigateToInsuranceClaim}
                    filterPreset={servicesFilterPreset}
                    onClearNavigationFilter={() => setServicesFilterPreset(undefined)}
                  />
                </Tabs.Panel>
              ) : null}

              {showBillingLedger ? (
                <Tabs.Panel value="statements" pt="md">
                  <StatementsPanel summary={summary} />
                </Tabs.Panel>
              ) : null}

              <Tabs.Panel value="payment-methods" pt="md">
                <PaymentMethodsPanel summary={summary} onRefresh={refresh} />
              </Tabs.Panel>

              {showBillingLedger ? (
                <Tabs.Panel value="client-payments" pt="md">
                  <ClientPaymentsPanel
                    summary={summary}
                    onServiceNavigate={navigateToServices}
                  />
                </Tabs.Panel>
              ) : null}

              {showBillingLedger ? (
                <Tabs.Panel value="insurance-payments" pt="md">
                  <InsurancePaymentsPanel
                    summary={summary}
                    filterPreset={insuranceFilterPreset}
                    onClearNavigationFilter={() => setInsuranceFilterPreset(undefined)}
                  />
                </Tabs.Panel>
              ) : null}
            </Tabs>
          </>
        )}
      </Stack>
    </ClientPortalLayout>
  );
}
